import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { apiClient, API } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableHead, TableHeader, TableRow, TableBody, TableCell } from "../components/ui/table";
import { buildWelcomeMessage, buildWhatsappUrl } from "../lib/welcomeMessage";

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("sircs_admin_token") || "");
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [tests, setTests] = useState([]);
  const [masterclass, setMasterclass] = useState([]);
  const [leads, setLeads] = useState([]);

  const isAuthed = !!token;

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await apiClient.post("/admin/login", creds);
      localStorage.setItem("sircs_admin_token", r.data.token);
      setToken(r.data.token);
      toast.success("Acceso concedido");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Credenciales inválidas");
    } finally {
      setLoading(false);
    }
  };

  const logout = React.useCallback(() => {
    localStorage.removeItem("sircs_admin_token");
    setToken("");
  }, []);

  useEffect(() => {
    if (!isAuthed) return;
    const fetchAll = async () => {
      try {
        const [a, t, m, l] = await Promise.all([
          apiClient.get("/admin/analytics"),
          apiClient.get("/admin/tests"),
          apiClient.get("/admin/masterclass"),
          apiClient.get("/admin/leads"),
        ]);
        setAnalytics(a.data);
        setTests(t.data.items);
        setMasterclass(m.data.items);
        setLeads(l.data.items);
      } catch (err) {
        if (err.response?.status === 401) {
          logout();
          toast.error("Sesión caducada");
        }
      }
    };
    fetchAll();
  }, [isAuthed, logout]);

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-bg-base text-ink flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="paper p-12 max-w-md w-full">
          <Link to="/" className="text-xs text-ink-muted hover:text-gold tracking-[0.2em] uppercase font-bold">← volver</Link>
          <h1 className="font-sans text-5xl text-ink mt-8 mb-3 font-medium tracking-[-0.025em]">Admin</h1>
          <p className="text-ink-muted text-base mb-10">Panel privado de Mentalidad SIRCS</p>
          <form onSubmit={login} className="space-y-6" data-testid="admin-login-form">
            <input
              type="email"
              className="input-line"
              placeholder="Email"
              value={creds.email}
              onChange={(e) => setCreds({ ...creds, email: e.target.value })}
              data-testid="admin-email"
            />
            <input
              type="password"
              className="input-line"
              placeholder="Contraseña"
              value={creds.password}
              onChange={(e) => setCreds({ ...creds, password: e.target.value })}
              data-testid="admin-password"
            />
            <button type="submit" className="btn-gold w-full" disabled={loading} data-testid="admin-login-btn">
              {loading ? "Verificando..." : "Entrar"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-base text-ink" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 md:px-12 py-8 md:py-12">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 md:mb-12 gap-4">
          <div>
            <p className="overline mb-3">◇ Panel administrativo</p>
            <h1 className="font-sans font-medium text-3xl md:text-5xl text-ink tracking-[-0.025em]">Mentalidad SIRCS</h1>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-sm md:text-base text-ink hover:text-gold font-semibold">Ver web →</Link>
            <button onClick={logout} className="text-sm md:text-base text-ink hover:text-gold font-semibold" data-testid="admin-logout">Cerrar sesión</button>
          </div>
        </div>

        {analytics && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-12">
            <StatCard label="Tests completados" value={analytics.totals.tests} testid="stat-tests" />
            <StatCard label="Reservas masterclass" value={analytics.totals.masterclass} testid="stat-masterclass" />
            <StatCard label="Leads mentoría" value={analytics.totals.leads} testid="stat-leads" />
          </div>
        )}

        {analytics && (
          <div className="paper p-10 mb-12" data-testid="pattern-distribution">
            <p className="overline mb-8">Distribución de patrones dominantes</p>
            <div className="space-y-5">
              {Object.entries(analytics.pattern_counts || {}).map(([k, v]) => {
                const total = Object.values(analytics.pattern_counts).reduce((a, b) => a + b, 0) || 1;
                const pct = Math.round((v / total) * 100);
                return (
                  <div key={k}>
                    <div className="flex justify-between text-base mb-2">
                      <span className="capitalize text-ink font-medium">{k}</span>
                      <span className="text-ink-muted font-mono num">{v} ({pct}%)</span>
                    </div>
                    <div className="h-1.5 w-full bg-ink/10">
                      <div className="h-full bg-gold" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              {Object.keys(analytics.pattern_counts || {}).length === 0 && (
                <p className="text-ink-muted text-base">Aún no hay tests completados.</p>
              )}
            </div>
          </div>
        )}

        <Tabs defaultValue="tests" className="w-full">
          <TabsList className="bg-bg-surface border border-ink/15 rounded-none p-1 gap-1 flex-wrap h-auto">
            <TabsTrigger value="tests" className="rounded-none font-semibold text-xs md:text-sm data-[state=active]:bg-ink data-[state=active]:text-bg-base" data-testid="tab-tests">Tests ({tests.length})</TabsTrigger>
            <TabsTrigger value="masterclass" className="rounded-none font-semibold text-xs md:text-sm data-[state=active]:bg-ink data-[state=active]:text-bg-base" data-testid="tab-masterclass">Masterclass ({masterclass.length})</TabsTrigger>
            <TabsTrigger value="leads" className="rounded-none font-semibold text-xs md:text-sm data-[state=active]:bg-ink data-[state=active]:text-bg-base" data-testid="tab-leads">Leads Mentoría ({leads.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="tests" className="mt-6">
            <DataTable
              items={tests}
              testid="table-tests"
              exportUrl={`${API}/admin/export/tests`}
              token={token}
              columns={[
                { key: "name", label: "Nombre" },
                { key: "email", label: "Email" },
                { key: "country", label: "País" },
                { key: "dominant_name", label: "Patrón" },
                { key: "dominant_percentage", label: "%", render: (v) => `${v}%` },
                { key: "created_at", label: "Fecha", render: (v) => new Date(v).toLocaleString("es-ES") },
              ]}
            />
          </TabsContent>

          <TabsContent value="masterclass" className="mt-6">
            <DataTable
              items={masterclass}
              testid="table-masterclass"
              exportUrl={`${API}/admin/export/masterclass`}
              token={token}
              columns={[
                {
                  key: "first_name",
                  label: "Nombre",
                  render: (v, row) => v || row.name?.split(" ")[0] || "—",
                },
                {
                  key: "last_name",
                  label: "Apellido",
                  render: (v, row) => v || row.name?.split(" ").slice(1).join(" ") || "—",
                },
                {
                  key: "phone",
                  label: "WhatsApp",
                  render: (v, row) => {
                    const num = v || `${row.phone_country_code || ""}${row.phone_number || ""}`;
                    if (!num) return <span className="text-ink-subtle">—</span>;
                    const waHref = buildWhatsappUrl(row);
                    const copyMessage = async () => {
                      try {
                        await navigator.clipboard.writeText(buildWelcomeMessage(row));
                        toast.success("Mensaje copiado");
                      } catch (_) {
                        toast.error("No se pudo copiar");
                      }
                    };
                    const toggleContacted = async (next) => {
                      try {
                        await apiClient.post(
                          `/admin/masterclass/${row.id}/contacted`,
                          { contacted: next },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        setMasterclass((prev) =>
                          prev.map((it) =>
                            it.id === row.id
                              ? { ...it, contacted: next, contacted_at: next ? new Date().toISOString() : null }
                              : it
                          )
                        );
                      } catch (_) {
                        toast.error("No se pudo actualizar el estado");
                      }
                    };
                    return (
                      <div className="flex flex-col gap-2">
                        <a
                          href={`tel:${num}`}
                          className="font-mono num text-ink hover:text-gold text-xs"
                          data-testid="admin-phone-link"
                        >
                          {num}
                        </a>
                        <div className="flex flex-wrap gap-2">
                          <a
                            href={waHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase text-gold hover:text-ink hover:bg-gold px-2.5 py-1.5 rounded transition-colors font-semibold border border-gold/50"
                            data-testid="admin-whatsapp-link"
                          >
                            Enviar msg →
                          </a>
                          <button
                            type="button"
                            onClick={copyMessage}
                            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase text-ink-muted hover:text-ink px-2.5 py-1.5 rounded transition-colors font-semibold border border-ink/25"
                            data-testid="admin-copy-msg"
                          >
                            Copiar
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleContacted(!row.contacted)}
                            className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase px-2.5 py-1.5 rounded transition-colors font-semibold border ${
                              row.contacted
                                ? "bg-ink text-bg-base border-ink"
                                : "text-ink-muted border-ink/25 hover:border-ink"
                            }`}
                            data-testid="admin-toggle-contacted"
                          >
                            {row.contacted ? "✓ Contactado" : "Marcar contactado"}
                          </button>
                        </div>
                      </div>
                    );
                  },
                },
                { key: "country", label: "País" },
                {
                  key: "utm_source",
                  label: "Origen",
                  render: (v, row) => {
                    const src = v || "organico";
                    const ref = row.referrer || "directo";
                    const displayRef = ref.length > 40 ? ref.slice(0, 40) + "…" : ref;
                    const isPaid = ["facebook", "instagram", "fb", "ig", "meta", "ads"]
                      .some((s) => src.toLowerCase().includes(s));
                    return (
                      <div className="flex flex-col gap-0.5" data-testid="admin-origen">
                        <span
                          className={`text-xs tracking-[0.10em] uppercase font-semibold ${
                            isPaid ? "text-gold" : "text-ink"
                          }`}
                          data-testid="admin-utm-source"
                        >
                          {src}
                        </span>
                        <span
                          className="text-[10px] text-ink-subtle font-mono break-all"
                          title={ref}
                          data-testid="admin-referrer"
                        >
                          {displayRef}
                        </span>
                      </div>
                    );
                  },
                },
                {
                  key: "created_at",
                  label: "Fecha",
                  render: (v) => new Date(v).toLocaleString("es-ES"),
                },
              ]}
            />
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            <DataTable
              items={leads}
              testid="table-leads"
              exportUrl={`${API}/admin/export/leads`}
              token={token}
              columns={[
                { key: "name", label: "Nombre" },
                { key: "email", label: "Email" },
                { key: "country", label: "País" },
                { key: "phone", label: "Teléfono" },
                { key: "message", label: "Mensaje" },
                { key: "created_at", label: "Fecha", render: (v) => new Date(v).toLocaleString("es-ES") },
              ]}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="paper p-8" data-testid={testid}>
      <p className="text-xs text-ink-muted tracking-[0.2em] uppercase font-bold">{label}</p>
      <p className="font-mono num text-6xl text-gold mt-4 leading-none">{value ?? 0}</p>
    </div>
  );
}

function DataTable({ items, columns, testid, exportUrl, token }) {
  const downloadCsv = async () => {
    try {
      const res = await fetch(exportUrl, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportUrl.split("/").pop() + ".csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Error al exportar");
    }
  };

  return (
    <div className="paper" data-testid={testid}>
      <div className="flex justify-end p-4 border-b border-ink/10">
        <button onClick={downloadCsv} className="btn-ghost-gold text-xs" data-testid={`${testid}-export`}>
          Exportar CSV
        </button>
      </div>

      {/* Desktop table (md+) */}
      <div className="hidden md:block overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-ink/15 hover:bg-transparent">
              {columns.map((c) => (
                <TableHead key={c.key} className="text-gold tracking-wide uppercase text-xs font-bold">{c.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={columns.length} className="text-center text-ink-muted py-16">Aún no hay datos.</TableCell></TableRow>
            ) : (
              items.map((it) => (
                <TableRow
                  key={it.id}
                  className={`border-ink/10 hover:bg-gold/8 transition-colors ${
                    it.contacted ? "opacity-55" : ""
                  }`}
                  data-testid={it.contacted ? "row-contacted" : "row-pending"}
                >
                  {columns.map((c) => (
                    <TableCell key={c.key} className="text-ink py-4 font-medium">
                      {c.render ? c.render(it[c.key], it) : (it[c.key] || "—")}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile card list (<md) */}
      <div className="md:hidden divide-y divide-ink/10" data-testid={`${testid}-mobile`}>
        {items.length === 0 ? (
          <p className="text-center text-ink-muted py-16 text-base">Aún no hay datos.</p>
        ) : (
          items.map((it) => (
            <div
              key={it.id}
              className={`p-5 space-y-3 hover:bg-gold/5 transition-colors ${
                it.contacted ? "opacity-55" : ""
              }`}
              data-testid={`${testid}-mobile-row`}
            >
              {columns.map((c) => {
                const rendered = c.render ? c.render(it[c.key], it) : (it[c.key] || "—");
                return (
                  <div key={c.key} className="flex flex-col gap-1">
                    <span className="text-[10px] tracking-[0.22em] uppercase text-ink-subtle font-semibold font-sans">
                      {c.label}
                    </span>
                    <div className="text-ink text-base font-medium break-words">
                      {rendered}
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
